import React, { useEffect, useRef } from 'react';
import { CELEBRATION_GRANT_PREFIX, type CelebrationKind } from '@utils/celebrationMarker';
import {
  ackIntent,
  getIntents,
  isIntentFresh,
  subscribeIntents,
  type IntentFor,
} from '@lib/overlay/intents';
import { acquire, type Grant } from '@lib/overlay/mutex';
import { useCelebrationStore } from '@stores/celebrationStore';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { openFirstGoingWelcome } from '@hooks/useOnboarding';
import { useLatestRef } from '@hooks/useLatestRef';
import { useSelfReportedRegistration } from '@hooks/useSelfReportedRegistration';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import type { SelfReportedRegistration } from '@models/api';
import type { RegistrationCelebration } from '@utils/registrationCelebration';
import { isEventPinCelebration, type EventPinCelebration } from '@utils/eventPinCelebration';
import { logger } from '@utils/logger';
import { isCompleted, markCompleted } from '@utils/onboardingProgress';
import { useAuthStore } from '@stores/authStore';
import {
  OnboardingFlowId,
  EventGoingWelcomeVariant,
  CelebrationVariant,
} from '@config/onboarding-flows';
import { SignupCelebrationDrawer } from './SignupCelebrationDrawer';
import { TicketedGoingCelebrationDrawer } from './TicketedGoingCelebrationDrawer';
import { EventGoingCelebrationDrawer } from './EventGoingCelebrationDrawer';
import { EventPinSheet, type EventPinSheetHandle } from './EventPinSheet';
import { ENTRANCE_GUARD_MS } from './entranceGuard';

// Maps each intent kind to the once-ever guard flow that gates it (see
// onboardingProgress). New flow ids rather than reusing EventGoingWelcome —
// so the celebration isn't suppressed for veterans who already completed the
// OLD generic first-RSVP welcome drawer. `registration_check` has no guard: the server decides
// whether to ask, and un-Going then re-Going must ask again.
const guardFlow: Record<CelebrationKind, OnboardingFlowId | null> = {
  signup: OnboardingFlowId.SignupCelebration,
  event_going: OnboardingFlowId.EventGoingCelebration,
  registration_check: null,
  event_pin: null,
};

// How long a drawer must have been on screen before a dismissal is treated as intentional.
// Long enough to outlast a double-tap (~150ms), short enough that a user deliberately
// dismissing never feels the button is dead.
//
// Deliberately SHORTER than vaul's own 500ms entrance, contrary to an earlier version of this
// comment. That is not a gap: vaul refuses to start a drag for 500ms after open (`shouldDrag`
// bails on `date.getTime() - openTime.current.getTime() < 500`), so no swipe can reach this
// guard at all — the only channel that does is a tap, and a tap on a drawer mid-entrance is
// the accidental double-tap this exists to swallow. Raising it to 500 would buy nothing and
// make a deliberate fast dismisser wait longer.
const MIN_VISIBLE_MS = ENTRANCE_GUARD_MS;

/** The once-ever guard, re-read live so stillNeeded sees a flip during a park. */
const alreadyCelebrated = (kind: CelebrationKind): boolean => {
  const flow = guardFlow[kind];
  return (
    flow !== null &&
    isCompleted(flow, CelebrationVariant.Default, useAuthStore.getState().user?.id ?? undefined)
  );
};

/**
 * App-level consumer of `celebration` intents (see src/lib/overlay/intents.ts). Mounted beside
 * GlobalOnboarding, outside the router: producers (signup success, post-auth attend, the
 * welcome event-CTA) post an intent and navigate first, and this host pops the drawer at the
 * destination. Once ever per kind, except registration_check (see guardFlow).
 *
 * Arbitrated by the overlay mutex (overlay-system-design §6.3): each claimed intent acquires
 * `celebration:<kind>`, the grant continuation opens the drawer through celebrationStore, and
 * `grant.closed` is the cleanup. The intent is acked at that same point — never earlier — so a
 * celebration still queued behind another survives a reload instead of living only in memory.
 *
 * `registration_check` (the ticketed Going celebration) differs in two ways. It carries a
 * payload the drawer renders. And it owns when the first-Going carousel opens: for
 * event_page/live_slot, when the drawer closes, answered or not, behind any celebration still
 * queued; a post-auth show marks it complete on show (as event_going does), so it never opens
 * there. The answer's PUT is owned here, not by the drawer, so a fire-and-forget save outlives
 * the drawer unmounting.
 *
 * `event_pin` (the PIN sheet, TECH-663) follows `registration_check`: it carries a payload, owns
 * the carousel's opening on close, and may end on the same ticket question, answered through the
 * same `answer`. Its sheet walks several steps under ONE grant, so the MIN_VISIBLE_MS veto guards
 * its first open only: the sheet's own `onClose` (it calls it after an answer or its timers, never
 * on the first card) is not vetoed, and Android back asks the sheet to dismiss, which on a PIN card
 * is a request for access and leaves the sheet open, and on an answer with a step after it moves on.
 */
export const CelebrationHost: React.FC = () => {
  const open = useCelebrationStore(s => s.open);
  const { trackEvent } = useAmplitudeTracking();
  const saveAnswer = useSelfReportedRegistration();
  // The effect below runs once, so it reaches the live tracker through this ref.
  const trackEventRef = useLatestRef(trackEvent);
  // The grant whose ticketed drawer was answered, read when that grant ends.
  const answeredGrantRef = useRef<Grant | null>(null);
  // The grant whose PIN sheet was dismissed on its ticket step unanswered (the sheet reports it).
  const ticketDismissedGrantRef = useRef<Grant | null>(null);
  // The PIN sheet on screen, for Android back (requestDismiss).
  const pinSheetRef = useRef<EventPinSheetHandle>(null);
  // The grant behind the currently visible drawer; close() dismisses through it.
  const grantRef = useRef<Grant | null>(null);
  // Grant ids this host has claimed and not yet seen end (waiting or on screen).
  const claimedIdsRef = useRef(new Set<string>());
  // When the CURRENT drawer became visible (the MIN_VISIBLE_MS veto clock).
  const openedAtRef = useRef(0);
  // The live effect run's check. A grant continuation outlives the run that claimed it (under
  // StrictMode, the first run's claim carries the drawer), so it re-checks through this, never
  // its own run's check, which would claim against an already-aborted signal.
  const checkRef = useRef<() => void>(() => undefined);

  // MIN_VISIBLE_MS is a host-side veto wrapping grant.dismiss (vetoes stay out
  // of the mutex, §6.3); a double-fired close is absorbed by exactly-once
  // settlement. The mount-only effect below passes the first render's close
  // as onBackButton; safe because close reads only refs, so back honours the
  // same MIN_VISIBLE_MS guard a tap does.
  const close = () => {
    if (Date.now() - openedAtRef.current < MIN_VISIBLE_MS) return;
    grantRef.current?.dismiss('user');
  };

  // The PIN sheet's own close. Not vetoed: the sheet calls it only after a step change (an answer
  // shown, its timers run out, a dismiss on the last answer or the ticket step), never on the first
  // PIN card, so there is no entrance double-tap to swallow, and a veto would strand the sheet on
  // `done`.
  const closePinSheet = () => {
    grantRef.current?.dismiss('user');
  };

  // Android back on the PIN sheet: the sheet decides what a dismiss means (on a PIN card, a request
  // for access, and the sheet stays open). The first open keeps the entrance veto close() has.
  // Reads only refs, so the mount-only effect may capture it once.
  const backOnPinSheet = () => {
    if (Date.now() - openedAtRef.current < MIN_VISIBLE_MS) return;
    pinSheetRef.current?.requestDismiss();
  };

  // The PIN sheet reports its ticket step dismissed unanswered just before it closes.
  const markTicketDismissed = () => {
    ticketDismissedGrantRef.current = grantRef.current;
  };

  // The member answered the ticket question: record it and close (the close continuation opens the
  // first-Going carousel, as for a close without an answer). Returns false when the tap is refused,
  // and the drawer then opens nothing and reports nothing. Refused: a tap inside the same
  // MIN_VISIBLE_MS veto as close(), since a swap between queued celebrations is synchronous and the
  // second tap of a double tap lands on the incoming drawer's answers; or a repeat on an answered
  // drawer. The PUT is not awaited: a save that blocked the close would block the drawer on a slow
  // network, and the drawer opens the ticket site right after this returns, in the same tap.
  const answer = (value: SelfReportedRegistration): boolean => {
    if (Date.now() - openedAtRef.current < MIN_VISIBLE_MS) return false;
    // The ticketed drawer's question, or the one the PIN sheet ends on.
    const { open: onScreen } = useCelebrationStore.getState();
    const shown =
      onScreen?.kind === 'registration_check'
        ? onScreen.registration
        : onScreen?.kind === 'event_pin'
          ? onScreen.eventPin.registration
          : null;
    const grant = grantRef.current;
    if (!shown || !grant || answeredGrantRef.current === grant) return false;
    answeredGrantRef.current = grant;
    saveAnswer.mutate({ eventId: shown.event_id, answer: value });
    grant.dismiss('user');
    return true;
  };

  // Driven by subscribeIntents, not the useIntents hook: this effect runs an imperative
  // acquire/ack cycle, not a render. The effect checks once on mount — an intent posted before
  // a full-page auth reload, a native cold start, or before this app-level host even exists
  // (SPA navigation never remounts it) — and subscribes for every later post.
  useEffect(() => {
    const controller = new AbortController();

    const claim = (intent: IntentFor<'celebration'>) => {
      const { kind } = intent.payload;
      const ticket: RegistrationCelebration | undefined =
        intent.payload.kind === 'registration_check' ? intent.payload.registration : undefined;
      const pin: EventPinCelebration | undefined =
        intent.payload.kind === 'event_pin' ? intent.payload.eventPin : undefined;
      // A malformed PIN payload would claim the grant and render nothing (the sheet needs it), so
      // the grant would never end. The channel validates stored intents; this guards an in-memory
      // post too. Dropped before the queue, like a suppressed celebration.
      if (intent.payload.kind === 'event_pin' && !isEventPinCelebration(pin)) {
        logger.warn('[CelebrationHost] dropped a malformed event_pin intent', { id: intent.id });
        ackIntent(intent.id);
        return;
      }
      // A suppressed celebration never enters the queue; ack it now so busy goes idle at once
      // instead of leaving a dead intent for a later check to keep tripping over.
      if (alreadyCelebrated(kind)) {
        ackIntent(intent.id);
        return;
      }
      const id = `${CELEBRATION_GRANT_PREFIX}${kind}`;
      if (claimedIdsRef.current.has(id)) return; // already claimed (a join would add nothing)
      claimedIdsRef.current.add(id);
      void acquire(id, controller.signal, {
        // Re-checked when the grant comes up, including after a takeover clears.
        stillNeeded: () => !alreadyCelebrated(kind),
        onBackButton: pin ? backOnPinSheet : close,
      }).then(grant => {
        if (grant.settledReason !== null) {
          claimedIdsRef.current.delete(id);
          // Stale: the once-ever guard was spent while this was parked, so it will never show;
          // ack it. Any other reason is this host's own withdrawal, and the intent stays owed.
          if (grant.settledReason === 'stale') ackIntent(intent.id);
          return;
        }
        grantRef.current = grant;
        openedAtRef.current = Date.now();
        // Marked before the drawer renders: a crash during render loses this one
        // celebration rather than risking a replay loop on every later mount.
        const flow = guardFlow[kind];
        if (flow !== null) {
          markCompleted(
            flow,
            CelebrationVariant.Default,
            useAuthStore.getState().user?.id ?? undefined,
          );
        }
        if (
          kind === 'event_going' ||
          ticket?.surface === 'post_auth' ||
          pin?.surface === 'post_auth'
        ) {
          // This drawer replaces the generic first-RSVP EventGoingWelcome (D10); so does the
          // post-auth ticketed one (TECH-575: mark it complete on show), and the post-auth PIN
          // sheet, which stands in for either of them (TECH-663).
          // Deliberately UNSCOPED: its reader, openFirstGoingWelcome, calls
          // isCompleted with no contextId, so a user-scoped key would stop suppressing it.
          markCompleted(OnboardingFlowId.EventGoingWelcome, EventGoingWelcomeVariant.Default);
        }
        // Acked here, at the grant, not at claim: an intent must outlive an unmount while
        // still queued, so it survives a reload until the moment it is actually on screen.
        ackIntent(intent.id);
        useCelebrationStore.getState().show(intent.payload);
        void grant.closed.then(reason => {
          claimedIdsRef.current.delete(id);
          if (grantRef.current === grant) grantRef.current = null;
          useCelebrationStore.getState().clear(kind);
          // A same-id intent posted while this claim held the slot never got past claim()'s
          // dedupe check; re-check now so it isn't left orphaned until an unrelated intent
          // change happens to trigger the subscription (e.g. after an in-tab account switch).
          // Through checkRef: this continuation may belong to an effect run that has since
          // been torn down, and only the live run's check can claim. Before the carousel below,
          // so a celebration still waiting (a second ticket question) queues ahead of it.
          checkRef.current();
          // The member closed the ticketed drawer: an answer, or X, swipe, outside, Escape, back.
          // A withdrawal (this host unmounting) is neither, so it is left alone.
          if ((ticket || pin) && (reason === 'user' || reason === 'back-button')) {
            // Nothing was saved and the member stays Going. The PIN sheet says so itself: it may
            // close long before its ticket step, or with no ticket question at all.
            const dismissed = ticket
              ? answeredGrantRef.current !== grant
              : ticketDismissedGrantRef.current === grant;
            const asked = ticket ?? pin?.registration;
            if (dismissed && asked) {
              trackEventRef.current(ANALYTICS_EVENTS.event_registration_sheet_dismissed, {
                event_id: asked.event_id,
                phase: asked.phase,
                surface: asked.surface,
              });
            }
            // TECH-575: the first-Going carousel comes after the celebration closes, answered or
            // not. openFirstGoingWelcome is a no-op once the carousel is complete, which a post-auth
            // question marked on show (as event_going does), so it never opens there.
            openFirstGoingWelcome();
          }
        });
      });
    };

    const check = () => {
      const now = Date.now();
      for (const intent of getIntents()) {
        if (intent.surface !== 'celebration' || !isIntentFresh(intent, now)) continue;
        claim(intent);
      }
    };

    // After StrictMode's cleanup withdrew the previous run's claims, re-join them
    // before the mutex's deferred claim check, so nothing waiting or on screen is lost.
    for (const id of claimedIdsRef.current) void acquire(id, controller.signal);
    checkRef.current = check;
    check();
    const unsubscribe = subscribeIntents(check);
    return () => {
      unsubscribe();
      // On a real unmount nothing re-joins, so every claim this host holds ends. Any intent
      // still queued stays pending by design (claim() above never acks on withdrawal), so
      // isCelebrationBusy() stays true through it until a later mount claims it or the TTL prunes it.
      controller.abort();
    };
  }, [trackEventRef]);

  switch (open?.kind) {
    case 'signup':
      return <SignupCelebrationDrawer onClose={close} />;
    case 'event_going':
      return <EventGoingCelebrationDrawer source={open.source} onClose={close} />;
    case 'event_pin':
      return (
        <EventPinSheet
          // A second event's sheet starts from its first card, never from the last one's step.
          key={open.eventPin.event_id}
          ref={pinSheetRef}
          eventPin={open.eventPin}
          source={open.source}
          onAnswer={answer}
          onClose={closePinSheet}
          onTicketDismissed={markTicketDismissed}
        />
      );
    case 'registration_check':
      return (
        <TicketedGoingCelebrationDrawer
          registration={open.registration}
          source={open.source}
          onAnswer={answer}
          onClose={close}
        />
      );
    default:
      return null;
  }
};
